import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, AlertTriangle, CheckCircle, Info, TrendingUp, TrendingDown, Clock, Mail, MessageCircle, Award, FileBarChart, UserPlus, X, Check, Archive, Filter, Search, Video, Send, ThumbsUp, Star, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
type AlertType = 'urgent' | 'warning' | 'info' | 'success';
type AlertCategory = 'learner' | 'system' | 'achievement' | 'report';
interface Alert {
  id: number;
  type: AlertType;
  category: AlertCategory;
  title: string;
  message: string;
  timestamp: string;
  date: Date;
  read: boolean;
  actionable: boolean;
  completed?: boolean;
  deleted?: boolean;
  learnerName?: string;
  learnerEmail?: string;
  module?: string;
}
export function AlertsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | AlertType>('all');
  const [selectedCategory, setSelectedCategory] = useState<'all' | AlertCategory | 'completed' | 'deleted'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'unread' | 'all'>('all');
  const [dateRange, setDateRange] = useState<'all' | 'today' | '7days' | '30days'>('all');
  const [activeCard, setActiveCard] = useState<'total' | 'urgent' | 'completed' | 'deleted' | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([{
    id: 1,
    type: 'urgent',
    category: 'learner',
    title: 'Learner Struggling - Immediate Attention Required',
    message: 'John Smith has failed JavaScript Basics 3 times with declining scores (68% → 65% → 58%). High misinformation rate detected.',
    timestamp: '5 minutes ago',
    date: new Date(Date.now() - 5 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'John Smith',
    learnerEmail: 'john.smith@example.com',
    module: 'JavaScript Basics'
  }, {
    id: 2,
    type: 'urgent',
    category: 'learner',
    title: 'At-Risk Learner - No Activity for 7 Days',
    message: 'Michael Chen has been inactive for 7 days. Last assessment score was 62% in CSS Fundamentals.',
    timestamp: '1 hour ago',
    date: new Date(Date.now() - 60 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'Michael Chen',
    learnerEmail: 'mchen@example.com',
    module: 'CSS Fundamentals'
  }, {
    id: 6,
    type: 'urgent',
    category: 'learner',
    title: 'CSS Fundamentals has 68% failure rate across all learners',
    message: '42 of 62 learners (68%) are failing CSS Fundamentals on first attempt. Average score is 54%, well below the 70% passing threshold. Common struggle areas: Flexbox (82% error rate), Grid Layout (76% error rate), and Positioning (71% error rate).',
    timestamp: '30 minutes ago',
    date: new Date(Date.now() - 30 * 60 * 1000),
    read: false,
    actionable: true,
    module: 'CSS Fundamentals'
  }, {
    id: 3,
    type: 'success',
    category: 'achievement',
    title: 'Outstanding Performance - Recognition Opportunity',
    message: 'Sarah Johnson achieved 95%+ on all 4 assessments on first attempt. Completed course 2 weeks ahead of schedule.',
    timestamp: '2 hours ago',
    date: new Date(Date.now() - 2 * 60 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'Sarah Johnson',
    learnerEmail: 'sarah.j@example.com',
    module: 'All Modules'
  }, {
    id: 4,
    type: 'warning',
    category: 'learner',
    title: 'Engagement Pattern Alert',
    message: 'Lisa Wong is taking unusually long between attempts (10+ days). Average score is 75% but engagement suggests time management issues.',
    timestamp: '5 hours ago',
    date: new Date(Date.now() - 5 * 60 * 60 * 1000),
    read: true,
    actionable: true,
    learnerName: 'Lisa Wong',
    learnerEmail: 'lisa.w@example.com',
    module: 'Responsive Design'
  }, {
    id: 5,
    type: 'info',
    category: 'system',
    title: 'Monthly Analytics Report Generated',
    message: 'Your monthly analytics report for December 2024 is ready for download.',
    timestamp: '1 day ago',
    date: new Date(Date.now() - 24 * 60 * 60 * 1000),
    read: true,
    actionable: false
  }, {
    id: 7,
    type: 'info',
    category: 'system',
    title: 'Scheduled Platform Maintenance',
    message: 'Platform maintenance is scheduled for Saturday, January 18th from 2:00 AM – 6:00 AM EST. During this window, assessments and reporting features will be temporarily unavailable. Please plan accordingly.',
    timestamp: '3 hours ago',
    date: new Date(Date.now() - 3 * 60 * 60 * 1000),
    read: false,
    actionable: false
  }]);
  // Modal states
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const [meetingModal, setMeetingModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const [kudosModal, setKudosModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const markAsRead = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      read: true
    } : alert));
  };
  const markAllAsRead = () => {
    setAlerts(alerts.map(alert => alert.category === 'system' ? {
      ...alert,
      read: true
    } : {
      ...alert,
      completed: true,
      read: true
    }));
  };
  const completeAlert = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      completed: true,
      read: true
    } : alert));
  };
  const softDeleteAlert = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      deleted: true,
      read: true
    } : alert));
  };
  const deleteAlert = (id: number) => {
    setAlerts(alerts.filter(alert => alert.id !== id));
  };
  const filteredAlerts = alerts.filter(alert => {
    // Handle category-based status filters (Completed/Deleted from dropdown)
    if (selectedCategory === 'completed') {
      if (!alert.completed) return false;
    } else if (selectedCategory === 'deleted') {
      if (!alert.deleted) return false;
    } else if (activeCard === 'completed') {
      if (!alert.completed) return false;
    } else if (activeCard === 'deleted') {
      if (!alert.deleted) return false;
    } else {
      // Hide completed and deleted alerts from normal views
      if (alert.completed || alert.deleted) return false;
    }
    // Filter by read status based on view mode
    if (viewMode === 'unread' && alert.read) return false;
    const matchesType = selectedFilter === 'all' || alert.type === selectedFilter;
    const matchesCategory = selectedCategory === 'all' || selectedCategory === 'completed' || selectedCategory === 'deleted' || alert.category === selectedCategory;
    const matchesSearch = alert.title.toLowerCase().includes(searchQuery.toLowerCase()) || alert.message.toLowerCase().includes(searchQuery.toLowerCase()) || alert.learnerName && alert.learnerName.toLowerCase().includes(searchQuery.toLowerCase());
    // Date range filter
    let matchesDate = true;
    if (dateRange !== 'all') {
      const now = new Date();
      const alertDate = alert.date;
      if (dateRange === 'today') {
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        matchesDate = alertDate >= startOfDay;
      } else if (dateRange === '7days') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        matchesDate = alertDate >= sevenDaysAgo;
      } else if (dateRange === '30days') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        matchesDate = alertDate >= thirtyDaysAgo;
      }
    }
    return matchesType && matchesCategory && matchesSearch && matchesDate;
  });
  const unreadCount = alerts.filter(a => !a.read && !a.completed && !a.deleted).length;
  const readCount = alerts.filter(a => a.read).length;
  const urgentCount = alerts.filter(a => a.type === 'urgent' && !a.read && !a.completed && !a.deleted).length;
  const completedCount = alerts.filter(a => a.completed).length;
  const deletedCount = alerts.filter(a => a.deleted).length;
  const resetFilters = () => {
    setSelectedFilter('all');
    setSelectedCategory('all');
    setSearchQuery('');
    setDateRange('all');
  };
  const handleCardClick = (card: 'total' | 'urgent' | 'completed' | 'deleted') => {
    if (activeCard === card) {
      // Toggle off — reset to defaults
      setActiveCard(null);
      setViewMode('all');
      resetFilters();
      return;
    }
    setActiveCard(card);
    resetFilters();
    switch (card) {
      case 'total':
        setViewMode('all');
        break;
      case 'urgent':
        setViewMode('all');
        setSelectedFilter('urgent');
        break;
      case 'completed':
        setViewMode('all');
        break;
      case 'deleted':
        setViewMode('all');
        break;
    }
  };
  const getAlertIcon = (type: AlertType) => {
    switch (type) {
      case 'urgent':
        return <AlertTriangle size={20} className="text-[#DB3725]" data-id="element-1926" data-mp="l83smayyhg99" />;
      case 'warning':
        return <Clock size={20} className="text-yellow-600" data-id="element-1927" data-mp="3kt8sy4m6bpmq" />;
      case 'success':
        return <CheckCircle size={20} className="text-[#007558]" data-id="element-1928" data-mp="3otj744k4dmco" />;
      case 'info':
        return <Info size={20} className="text-[#30718D]" data-id="element-1929" data-mp="23orehmdjgnuu" />;
    }
  };
  const getAlertColor = (type: AlertType) => {
    switch (type) {
      case 'urgent':
        return 'border-[#DB3725] bg-[#DB3725]/5';
      case 'warning':
        return 'border-yellow-500 bg-white';
      case 'success':
        return 'border-[#007558] bg-[#007558]/5';
      case 'info':
        return 'border-[#30718D] bg-[#30718D]/5';
    }
  };
  const getRecipientName = (alert: Alert) => alert.learnerName || 'Team';
  const getRecipientFirstName = (alert: Alert) => alert.learnerName ? alert.learnerName.split(' ')[0] : 'Team';
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1930" data-mp="1ty6jsa0zk3h7">
      {/* Send Message Modal */}
      <AnimatePresence data-id="element-1931" data-mp="1yrhpu8xz4g82">
        {messageModal.isOpen && messageModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMessageModal({
        isOpen: false,
        alert: null
      })} data-id="element-1932" data-mp="11blt7bsc3vep">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1933" data-mp="tr1hzo9y0z16">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1934" data-mp="ftuw3ktxak7q">
                <div className="flex items-center gap-3" data-id="element-1935" data-mp="ayyvhwameoj0">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-1936" data-mp="630bsm08luox">
                    <Mail size={24} className="text-white" data-id="element-1937" data-mp="146x4q97rxn6" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1938" data-mp="3t6y2thjgwbdo">
                    Send Message
                  </h2>
                </div>
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1939" data-mp="3o9ogwbvaehul">
                  <X size={20} className="text-gray-500" data-id="element-1940" data-mp="3imw2y2s4onf1" />
                </button>
              </div>
              <div className="p-6" data-id="element-1941" data-mp="34nmnx6fnrzcn">
                <p className="text-gray-600 mb-1" data-id="element-1942" data-mp="39kmox2xnibww">
                  Your message has been successfully sent to
                </p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1943" data-mp="164h92bmgydt">
                  {getRecipientName(messageModal.alert)}
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1944" data-mp="65z3utod71t2">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-1945" data-mp="3nijqkdqyp8if">Message Preview:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1946" data-mp="3si62mjv20ipn">
                    "Hi {getRecipientFirstName(messageModal.alert)}, I noticed
                    your recent assessment activity and wanted to reach out to
                    discuss your progress. Let's schedule a time to connect."
                  </p>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1947" data-mp="24mhtbkssd5il">
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1948" data-mp="24k58mzvn8h1u">
                  Cancel
                </button>
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1949" data-mp="1ln3twncaf2kp">
                  <Send size={18} data-id="element-1950" data-mp="1upwmctx7hkc4" />
                  Send
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Schedule Meeting Modal */}
      <AnimatePresence data-id="element-1951" data-mp="365bffmmans1y">
        {meetingModal.isOpen && meetingModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMeetingModal({
        isOpen: false,
        alert: null
      })} data-id="element-1952" data-mp="59arpu2herwf">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1953" data-mp="k6bjfxkoamuw">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1954" data-mp="12vrnuvzhazgl">
                <div className="flex items-center gap-3" data-id="element-1955" data-mp="17vdzx23km9nt">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-1956" data-mp="17srrl08uvq4j">
                    <Video size={24} className="text-white" data-id="element-1957" data-mp="1bttrzkeie883" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1958" data-mp="2wc4b5d007y9s">
                    Schedule Meeting
                  </h2>
                </div>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1959" data-mp="2ncr1okbvr9p2">
                  <X size={20} className="text-gray-500" data-id="element-1960" data-mp="3g2pu8ppa9oyh" />
                </button>
              </div>
              <div className="p-6" data-id="element-1961" data-mp="3b2lg36bul6bf">
                <p className="text-gray-600 mb-1" data-id="element-1962" data-mp="3b3n0i1er755t">Schedule a meeting with</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1963" data-mp="36bd3y2j4v6uk">
                  {getRecipientName(meetingModal.alert)}
                </p>
                <div className="space-y-4" data-id="element-1964" data-mp="31bh9ov08mts7">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1965" data-mp="2nl9g1brv0gq">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-1966" data-mp="3u1h4u24xsshv">Meeting Topic:</p>
                    <p className="text-gray-700 font-medium" data-id="element-1967" data-mp="1s4f9vh0x1e4g">
                      {meetingModal.alert.title}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1968" data-mp="1w5isuce9fq9w">
                    <div data-id="element-1969" data-mp="1if5kwa1dcbkk">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1970" data-mp="1iduiadjnt9v1">
                        Date
                      </label>
                      <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-1971" data-mp="18gg0oam02f15" />
                    </div>
                    <div data-id="element-1972" data-mp="zgt90gj2onla">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1973" data-mp="zhm6eqw560qr">
                        Time
                      </label>
                      <input type="time" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue="10:00" data-id="element-1974" data-mp="2tt9cxaoebpmz" />
                    </div>
                  </div>
                  <div data-id="element-1975" data-mp="38xlws3akoy6c">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1976" data-mp="38upygdutc7hd">
                      Duration
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-1977" data-mp="3hpnvnx4zu6ei">
                      <option data-id="element-1978" data-mp="3cwb5vjokvtk6">15 minutes</option>
                      <option selected data-id="element-1979" data-mp="3rmir275yzqok">30 minutes</option>
                      <option data-id="element-1980" data-mp="3rpelvmmitz8s">45 minutes</option>
                      <option data-id="element-1981" data-mp="3mp0i4grydbgk">1 hour</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1982" data-mp="4ht2hv9l126w">
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1983" data-mp="iby755w6b3b">
                  Cancel
                </button>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1984" data-mp="2qsiwssf0ulnx">
                  <Calendar size={18} data-id="element-1985" data-mp="34kyrn5xrrddr" />
                  Schedule
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Send Kudos Modal */}
      <AnimatePresence data-id="element-1986" data-mp="2cukljvvzz5cn">
        {kudosModal.isOpen && kudosModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setKudosModal({
        isOpen: false,
        alert: null
      })} data-id="element-1987" data-mp="2gve5ke6bk009">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1988" data-mp="13qegzdzl1pd">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1989" data-mp="1g100cdqrpat">
                <div className="flex items-center gap-3" data-id="element-1990" data-mp="3tlo4svbkwxrc">
                  <div className="w-12 h-12 rounded-lg bg-[#007558] flex items-center justify-center" data-id="element-1991" data-mp="3on3afcj0d8at">
                    <ThumbsUp size={24} className="text-white" data-id="element-1992" data-mp="3jmp1ls9l1ubq" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1993" data-mp="3envpa36189d5">
                    Give Kudos
                  </h2>
                </div>
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1994" data-mp="3aov80p37des9">
                  <X size={20} className="text-gray-500" data-id="element-1995" data-mp="18w756guh0td0" />
                </button>
              </div>
              <div className="p-6" data-id="element-1996" data-mp="3bm34sz5kjvit">
                <p className="text-gray-600 mb-1" data-id="element-1997" data-mp="37l9k0enfawu5">Send kudos to celebrate</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1998" data-mp="37ndsl9w9ok0r">
                  {getRecipientName(kudosModal.alert)}
                </p>
                <div className="bg-[#007558]/5 rounded-lg p-4 border border-[#007558]/20 mb-4" data-id="element-1999" data-mp="2xw9gqj1w1fa5">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2000" data-mp="2swn2q7e8jv0a">
                    <Star size={18} className="text-[#007558]" data-id="element-2001" data-mp="2su0m7i9da55x" />
                    <p className="text-sm font-medium text-[#007558]" data-id="element-2002" data-mp="2nvqop1066sh5">
                      Achievement:
                    </p>
                  </div>
                  <p className="text-gray-700" data-id="element-2003" data-mp="2ewvhpmpaxecs">{kudosModal.alert.title}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-2004" data-mp="i41zx1dnyoao">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-2005" data-mp="i1zb2ko8z17j">Kudos Message:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-2006" data-mp="d60mavz0bh6l">
                    "Congratulations {getRecipientFirstName(kudosModal.alert)}!
                    🎉 Your hard work and dedication have really paid off. Keep
                    up the amazing work!"
                  </p>
                </div>
                <div className="mt-4" data-id="element-2007" data-mp="ugnvyz7agjpu">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2008" data-mp="ze610ei6qesk">
                    Add a personal note (optional)
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007558] focus:border-transparent resize-none" rows={3} placeholder="Add your personal message..." data-id="element-2009" data-mp="1s8m0r30krld" />
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-2010" data-mp="fqq2q4ui53ts">
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-2011" data-mp="1n3rworzcmbvt">
                  Cancel
                </button>
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-2012" data-mp="3j6wm622wei37">
                  <ThumbsUp size={18} data-id="element-2013" data-mp="34hgzoyaegytb" />
                  Send Kudos
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2014" data-mp="3e70jq79umypr">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2015" data-mp="3e8leny02sjo5">
          <div className="flex items-center h-16" data-id="element-2016" data-mp="39fiqy6hzc4fv">
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2017" data-mp="2lqzklwihq1qf">LOGO</div>

            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2018" data-mp="2lp6dkg3ytfhw">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2019" data-mp="2gr4sjpmzb63s">
                <Home size={18} data-id="element-2020" data-mp="1113wl5b7s20o" />
                <span className="hidden sm:inline" data-id="element-2021" data-mp="111dnqw7zwf7y">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2022" data-mp="na8ineluyuq8">
                <BarChart3 size={18} data-id="element-2023" data-mp="1fxbg03esszew" />
                <span className="hidden sm:inline" data-id="element-2024" data-mp="1fycrua3n1pxo">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2025" data-mp="1jyescfxn0dbd">
                <FileText size={18} data-id="element-2026" data-mp="1ay8l3bsj89kn" />
                <span className="hidden sm:inline" data-id="element-2027" data-mp="1ax6x66qf3sli">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2028" data-mp="3qouqzta4x503">
                <UserCog size={18} data-id="element-2029" data-mp="etrqw2b6lm6c" />
                <span className="hidden sm:inline" data-id="element-2030" data-mp="eut1y6s768o2">Users</span>
              </Link>
            </nav>

            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2031" data-mp="a0ymxgnjehvb">
              <Link data-phoebe-nav="/alerts" to="/alerts" className="relative p-2 text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2032" data-mp="604ty8v8mcvk">
                <Bell size={20} data-id="element-2033" data-mp="13o67frya0j0" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2034" data-mp="xsdt5kifpjbu"></span>}
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2035" data-mp="trttoaae23mj">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2036" data-mp="or5vn4nqsmit">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2037" data-mp="jufdmbsdngur">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2038" data-mp="34gz63hbu6evf">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2039" data-mp="2zdgeni9ymo1k" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2040" data-mp="34cjb4ebvjlxx">
                    <div className="py-2" data-id="element-2041" data-mp="3952p3ceh4j39">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2042" data-mp="3d54rjns19gjl">
                        <Users size={18} className="text-gray-600" data-id="element-2043" data-mp="3i2mr4nk5ujex" />
                        <span className="font-medium text-gray-900" data-id="element-2044" data-mp="3n3114b5bo3fl">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2045" data-mp="3s018qqhtle8t">
                        <Home size={18} className="text-gray-600" data-id="element-2046" data-mp="1xnktx1aykrsc" />
                        <span className="font-medium text-gray-900" data-id="element-2047" data-mp="1pnoks4wmtbke">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2048" data-mp="1kqen61z39tlh">
                        <UserCog size={18} className="text-gray-600" data-id="element-2049" data-mp="1um892byp6zfx" />
                        <span className="font-medium text-gray-900" data-id="element-2050" data-mp="1up449sj5va0o">
                          Admin Hub
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2051" data-mp="23mwmp5k6ixsa"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2052" data-mp="2iclqz9nkjhws">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2053" data-mp="2de0yjwek62iw">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2054" data-mp="2nd9s4o5ihf4q"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2055" data-mp="3610pp3hy1ybi"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2056" data-mp="3az29jqn721o1"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2057" data-mp="2wzu6gmnwu9ek">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2058" data-mp="2s0zofmxroovi">
        {/* Header */}
        <div data-phoebe-tour="alerts" className="mb-8" data-id="element-2059" data-mp="2e9ulobz3rggy">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-2060" data-mp="2i9vbstvkdg3u">
            <div data-id="element-2061" data-mp="24adlga2mdusd">
              <div className="flex items-center gap-2 mb-2" data-id="element-2062" data-mp="249u2cq18h0cr">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-2063" data-mp="24cpxy7plzcy1">
                  Action Items
                </h1>
                <AIInsightPopover align="left" headline="Start with the urgent items to unblock learners" insightBeforeMetric="There are" highlightedMetric="2 urgent items" insightAfterMetric=", and the oldest open item has waited 6 days." explanation="Clearing these first removes the blockers, then the remaining reminders can be handled in a single batch." insightId={14} data-id="element-2064" data-mp="2orztub87hsze" />
              </div>
              <p className="text-gray-600 text-sm sm:text-base" data-id="element-2065" data-mp="210c9yd9d3i6m">
                Stay updated with important alerts and take action on learner
                needs
              </p>
            </div>
            <button onClick={markAllAsRead} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors self-start" data-id="element-2066" data-mp="1s2s5ywk477sb">
              <CheckCircle size={16} className="text-[#007558]" data-id="element-2067" data-mp="1i4cegsh6dwia" />
              Mark all as complete
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-2068" data-mp="2jtodx829ymd">
            <motion.button onClick={() => handleCardClick('total')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'total' ? 'border-[#30718D] ring-1 ring-[#30718D]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2069" data-mp="2lwlt2bjmu14">
              <div className="flex items-center justify-between" data-id="element-2070" data-mp="3jzijyrz2jp8q">
                <div data-id="element-2071" data-mp="3ora3iijt4upd">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2072" data-mp="3orjr5zhdqprw">
                    {alerts.filter(a => !a.completed && !a.deleted).length}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-2073" data-mp="2wazcgenufghp">
                    Total Active Alerts
                  </div>
                </div>
                <Bell size={24} className="text-[#30718D]" data-id="element-2074" data-mp="26zmgnaial550" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('urgent')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'urgent' ? 'border-[#DB3725] ring-1 ring-[#DB3725]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2075" data-mp="2ayvasgh8v513">
              <div className="flex items-center justify-between" data-id="element-2076" data-mp="1e9w1qz8u5z7u">
                <div data-id="element-2077" data-mp="1ebqkc6wmxf8r">
                  <div className="text-2xl font-bold text-[#DB3725]" data-id="element-2078" data-mp="1e82txgmhl8y9">
                    {urgentCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-2079" data-mp="14b7w6zy1fcl1">Urgent</div>
                </div>
                <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-2080" data-mp="19ajam2axm5zg" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('completed')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'completed' ? 'border-[#007558] ring-1 ring-[#007558]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2081" data-mp="po96paiqqs8y">
              <div className="flex items-center justify-between" data-id="element-2082" data-mp="18lio6m4i9vnk">
                <div data-id="element-2083" data-mp="14ls38z1nu9qy">
                  <div className="text-2xl font-bold text-[#007558]" data-id="element-2084" data-mp="14k76r3vscgqg">
                    {completedCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-2085" data-mp="1devf52wnqejh">Completed</div>
                </div>
                <CheckCircle size={24} className="text-[#007558]" data-id="element-2086" data-mp="1sbmexda8ts9m" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('deleted')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'deleted' ? 'border-gray-500 ring-1 ring-gray-500/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2087" data-mp="1ndb2qvwh75o8">
              <div className="flex items-center justify-between" data-id="element-2088" data-mp="2966sjxmbmm5b">
                <div data-id="element-2089" data-mp="2e2x88ju7rfq7">
                  <div className="text-2xl font-bold text-gray-500" data-id="element-2090" data-mp="2e3ff0aei5ccd">
                    {deletedCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-2091" data-mp="2i7em62dw6yt2">Deleted</div>
                </div>
                <Archive size={24} className="text-gray-500" data-id="element-2092" data-mp="2n2u0swjq4yqj" />
              </div>
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6" data-id="element-2093" data-mp="srxdxv7vsbro">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 flex-wrap" data-id="element-2094" data-mp="xqrvyuy0xwaq">
            <div className="flex-1 min-w-0 relative" data-id="element-2095" data-mp="q6zdmahuijjs">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2096" data-mp="lfr5dhs95487" />
              <input type="text" placeholder="Search notifications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm" data-id="element-2097" data-mp="gg4rd64lnjyc" />
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-2098" data-mp="145fn2973eo5m">
              <Filter size={16} className="text-gray-600" data-id="element-2099" data-mp="1444i461wvacx" />
              <select value={selectedFilter} onChange={e => setSelectedFilter(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-2100" data-mp="z5ktfxvxnkgv">
                <option value="all" data-id="element-2101" data-mp="3exigrhzwlm5w">All Types</option>
                <option value="urgent" data-id="element-2102" data-mp="3ewp7a99k8ik2">Urgent</option>
                <option value="warning" data-id="element-2103" data-mp="17mjtj56jqxp1">Warning</option>
                <option value="success" data-id="element-2104" data-mp="17kqm3no409fz">Success</option>
                <option value="info" data-id="element-2105" data-mp="1celcf9y7zmo1">Info</option>
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-2106" data-mp="xpf5t0x1yu64">
              <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-2107" data-mp="12nqe377p8wm8">
                <option value="all" data-id="element-2108" data-mp="1vbmk52j5s0vo">All Categories</option>
                <option value="learner" data-id="element-2109" data-mp="1gfwvewml99n9">Learner Alerts</option>
                <option value="system" data-id="element-2110" data-mp="1ghhn8ehiqehf">System Alerts</option>
                <option value="achievement" data-id="element-2111" data-mp="1lcx47fatoqi2">Kudos</option>
                <option value="completed" data-id="element-2112" data-mp="50863ylv3yja">Completed</option>
                <option value="deleted" data-id="element-2113" data-mp="4ynfgk2o4zqp">Deleted</option>
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-2114" data-mp="a0cro31mnjgd">
              <Calendar size={16} className="text-gray-600" data-id="element-2115" data-mp="2o5rekeg6mlso" />
              <select value={dateRange} onChange={e => setDateRange(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-2116" data-mp="2j7pv3seugkgo">
                <option value="all" data-id="element-2117" data-mp="2t6f4ewsocwlb">All Time</option>
                <option value="today" data-id="element-2118" data-mp="25g2yfrgzp9xd">Today</option>
                <option value="7days" data-id="element-2119" data-mp="25gun0ic667e5">Last 7 Days</option>
                <option value="30days" data-id="element-2120" data-mp="20hrnteiveve3">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Alerts List */}
        <div className="space-y-3" data-id="element-2121" data-mp="2ear5nn64l1ot">
          <AnimatePresence mode="popLayout" data-id="element-2122" data-mp="29heh1d1g4uw2">
            {filteredAlerts.map((alert, index) => <motion.div key={alert.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            delay: index * 0.05
          }} className={`bg-white rounded-xl shadow-sm border-l-4 overflow-hidden ${getAlertColor(alert.type)} ${!alert.read ? 'border-l-4' : 'border-l-2 opacity-75'}`} data-id="element-2123" data-mp="1lsc067emz62m" data-mpi={index}>
                <div className="p-4 sm:p-6" data-id="element-2124" data-mp="3qltl4vijv20u">
                  <div className="flex items-start gap-3 sm:gap-4" data-id="element-2125" data-mp="3vk4wxbsenmlp">
                    <div className="flex-shrink-0 mt-1" data-id="element-2126" data-mp="3vmh9tajb36rw">
                      {getAlertIcon(alert.type)}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-2127" data-mp="32vh32rtlbowj">
                      {/* Title and Actions - Stack on mobile */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-2" data-id="element-2128" data-mp="37uk8iddhfx51">
                        <div className="flex-1" data-id="element-2129" data-mp="3cvhs8ue54z8n">
                          <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-2130" data-mp="n4uxkbfwgvhm">
                            <h3 className="text-sm sm:text-base font-bold text-gray-900" data-id="element-2131" data-mp="n6xjaja0s2gj">
                              {alert.title}
                            </h3>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed" data-id="element-2132" data-mp="2cnjconpq250z">
                            {alert.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2133" data-mp="22tkeu0wol7eu">
                          {!alert.completed && !alert.deleted && <>
                              {alert.category !== 'system' && <button onClick={() => completeAlert(alert.id)} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" title="Mark as completed" data-id="element-2134" data-mp="1sz29ysfj35tg">
                                  <CheckCircle size={14} className="text-[#007558]" data-id="element-2135" data-mp="1f02lplvxgsxt" />
                                  <span className="hidden sm:inline" data-id="element-2136" data-mp="3eaaqhzjvirfu">
                                    Mark Completed
                                  </span>
                                </button>}
                              <button onClick={() => softDeleteAlert(alert.id)} className="p-1.5 text-gray-400 hover:text-[#DB3725] hover:bg-[#DB3725]/10 rounded-lg transition-colors" title="Delete" data-id="element-2137" data-mp="2i7ey68nvs5fr">
                                <X size={16} data-id="element-2138" data-mp="28gk10993jlfj" />
                              </button>
                            </>}
                          {alert.completed && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium bg-[#007558]/10 text-[#007558] rounded-lg" data-id="element-2139" data-mp="1zg5fp9w4u6c1">
                              <CheckCircle size={14} data-id="element-2140" data-mp="24cvsnocn4lt8" />
                              <span className="hidden sm:inline" data-id="element-2141" data-mp="1pj9dflwtugfx">
                                Completed
                              </span>
                            </span>}
                          {alert.deleted && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium bg-gray-100 text-gray-500 rounded-lg" data-id="element-2142" data-mp="3jx1ymcofju3r">
                              <Archive size={14} data-id="element-2143" data-mp="1d76zb4sur9es" />
                              <span className="hidden sm:inline" data-id="element-2144" data-mp="1d800lq91l6pj">Deleted</span>
                            </span>}
                        </div>
                      </div>

                      {/* Learner Info - Stack on mobile */}
                      {alert.learnerName && <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mb-3 text-sm" data-id="element-2145" data-mp="1i6jjtiw944e7">
                          <div className="flex items-center gap-2" data-id="element-2146" data-mp="1vx6m1l180cf1">
                            <Users size={14} className="text-gray-500" data-id="element-2147" data-mp="1rwwcjye7z3vf" />
                            <span className="font-medium text-gray-700" data-id="element-2148" data-mp="1ryz2kifdgx05">
                              {alert.learnerName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-2149" data-mp="20v81zx3cnvma">
                            <Mail size={14} className="text-gray-500" data-id="element-2150" data-mp="bj5wrudqmr5y" />
                            <span className="text-gray-600 text-xs sm:text-sm" data-id="element-2151" data-mp="6jtapbuoxo4f">
                              {alert.learnerEmail}
                            </span>
                          </div>
                          {alert.module && <div className="flex items-center gap-2" data-id="element-2152" data-mp="13istf4yk54ir">
                              <FileBarChart size={14} className="text-gray-500" data-id="element-2153" data-mp="1hhitmtgobkbc" />
                              <span className="text-gray-600 text-xs sm:text-sm" data-id="element-2154" data-mp="1hffunjxjmguh">
                                {alert.module}
                              </span>
                            </div>}
                        </div>}

                      {/* Action Buttons and Timestamp */}
                      <div className="pt-3 border-t border-gray-200" data-id="element-2155" data-mp="1mesrmxi4p4as">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-2156" data-mp="206yw3mbx5mue">
                          {alert.actionable ? <div className="flex flex-wrap items-center gap-2" data-id="element-2157" data-mp="1vba4c5655a8n">
                              <button onClick={() => setMessageModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors" data-id="element-2158" data-mp="2a6zglbliy906">
                                <Mail size={14} data-id="element-2159" data-mp="2euz606u4g60v" />
                                <span className="hidden sm:inline" data-id="element-2160" data-mp="2euh3isgv8vki">
                                  Send Message
                                </span>
                              </button>
                              <button onClick={() => setMeetingModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2161" data-mp="254e161nuu59i">
                                <Video size={14} data-id="element-2162" data-mp="1m6lbi128rru2" />
                                <span className="hidden sm:inline" data-id="element-2163" data-mp="1m6uydfrzpivj">
                                  Schedule Meeting
                                </span>
                              </button>
                              {alert.type === 'success' && <button onClick={() => setKudosModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border border-[#007558] bg-[#007558]/10 text-[#007558] rounded-lg hover:bg-[#007558]/20 transition-colors" data-id="element-2164" data-mp="182mcagcla7pj">
                                  <ThumbsUp size={14} data-id="element-2165" data-mp="1qyvwlle33uhz" />
                                  <span className="hidden sm:inline" data-id="element-2166" data-mp="1m0avljr0d6sh">
                                    Send Kudos
                                  </span>
                                </button>}
                            </div> : <div data-id="element-2167" data-mp="1c364r6m7wn5c" />}
                          <span className="flex items-center gap-1.5 text-xs text-gray-400 self-start sm:self-auto" data-id="element-2168" data-mp="1c24uv5gxu6p7">
                            <Clock size={12} data-id="element-2169" data-mp="5k80zbg2ezu6" />
                            {alert.timestamp}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </AnimatePresence>

          {filteredAlerts.length === 0 && <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200" data-id="element-2170" data-mp="2m88wqleojygm">
              {activeCard === 'completed' ? <>
                  <CheckCircle size={48} className="mx-auto text-[#007558] mb-4" data-id="element-2171" data-mp="2r0se7tghndqp" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2172" data-mp="2vy0ufoi2t7pb">
                    No completed alerts
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-2173" data-mp="34z8sc5gok6iv">
                    Alerts you mark as completed will appear here.
                  </p>
                  <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-2174" data-mp="39xabsri0q7uv">
                    <Bell size={16} data-id="element-2175" data-mp="360czg82iz9d0" />
                    View all alerts
                  </button>
                </> : activeCard === 'deleted' ? <>
                  <Archive size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2176" data-mp="314pc7y7lsqa5" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2177" data-mp="3f1b2oo553cuj">
                    No deleted alerts
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-2178" data-mp="2naohrgnoppfl">
                    Alerts you delete will appear here.
                  </p>
                  <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-2179" data-mp="2icwfbspeykx5">
                    <Bell size={16} data-id="element-2180" data-mp="23lvyp0eijkpx" />
                    View all alerts
                  </button>
                </> : <>
                  <Bell size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2181" data-mp="1zl3n40i653qh" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2182" data-mp="1pgdpg6tssls2">
                    No notifications found
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-2183" data-mp="1u9yhzjcmgnjv">
                    Try adjusting your filters or search query
                  </p>
                  {(selectedFilter !== 'all' || selectedCategory !== 'all' || dateRange !== 'all' || searchQuery || activeCard) && <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-2184" data-mp="288y5uosb8yez">
                      <X size={16} data-id="element-2185" data-mp="237sdis2kbd5z" />
                      Clear all filters
                    </button>}
                </>}
            </div>}
        </div>
      </div>
    </div>;
}